import { type KeyboardEventHandler, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import {
  useApplyUserCircleBoothformPatch,
  useCircleBoothApplyDetail,
  useCircleBoothform,
} from '~/features/circle-participation/queries/useCircleApply'
import { checkOverLargeBoothType } from '~/features/circle-participation/utils/function'
import {
  type BoothType,
  type DateType,
  type ZoneType,
} from '~/features/circle-participation/utils/type'
import { useOngoingEvent } from '~/features/main/queries/useMainContents'
import Back from '~/shared/assets/icons/Back'
import Check from '~/shared/assets/icons/Check'
import Button from '~/shared/components/buttons/Button'
import Tag from '~/shared/components/tags/Tag'
import theme from '~/shared/styles/theme'
import { checkPhoneNumberValidation } from '~/shared/utils/checkPhoneNumberValidation'

interface IForm {
  boothName: string
  dateType: keyof typeof DateType
  boothType: keyof typeof BoothType
  boothOptionType: string
  zoneType: keyof typeof ZoneType
  userName: string
  password: string
  userEmail: string
  userMobile: string
  nearBooth: string
  etc: string
}

function CircleApplyModifyPage() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { applicationId } = useParams()
  const [isSuperBooth, setIsSuperBooth] = useState<boolean>(false)
  const { data: ongoingEvent } = useOngoingEvent()
  const { data: circleBoothform, refetch } = useCircleBoothform(
    ongoingEvent?.data.eventInfo.id.toString() || '',
  )
  const { data: circleBoothApplyDetail } = useCircleBoothApplyDetail(
    applicationId || '',
  )
  const { mutate: userCircleBoothformPatchMutate } =
    useApplyUserCircleBoothformPatch()

  const { register, watch, handleSubmit, setValue } = useForm<IForm>({
    mode: 'onSubmit',
    defaultValues: {},
  })

  const { booth_type_list = [], zone_type_list = [] } =
    circleBoothform?.data.boothApplication || {}

  const [dateType, zoneType, boothType, boothOptionType, userMobile] = watch([
    'dateType',
    'zoneType',
    'boothType',
    'boothOptionType',
    'userMobile',
  ])

  const onSubmit: SubmitHandler<IForm> = (data) => {
    const userBoothApplication = {
      ...circleBoothApplyDetail?.data.userBoothApplication,
      id: applicationId || '',
      booth_name: data.boothName,
      date_type: data.dateType,
      booth_type: data.boothType,
      booth_option_type: data.boothOptionType,
      zone_type: data.zoneType,
      user_name: data.userName,
      user_email: data.userEmail,
      user_mobile: data.userMobile,
      etc: data.etc,
    }

    if (isSuperBooth && !data.boothOptionType) {
      enqueueSnackbar('희망하는 초대형 부스 종류를 선택해 주세요.', {
        variant: 'error',
      })

      return
    }

    userCircleBoothformPatchMutate(
      { userBoothApplication },
      {
        onSuccess: () => {
          navigate(`/circle-participation/apply-list/${applicationId}`)
        },
        onError: () => {
          enqueueSnackbar('수정에 실패했습니다.', { variant: 'error' })
        },
      },
    )
  }
  const checkKeyDown: KeyboardEventHandler<HTMLFormElement> = (e) => {
    if (e.key === 'Enter') e.preventDefault()
  }
  useEffect(() => {
    refetch()
  }, [ongoingEvent])
  useEffect(() => {
    if (circleBoothApplyDetail) {
      setValue(
        'dateType',
        circleBoothApplyDetail.data.userBoothApplication.date_type,
      )
      setValue(
        'boothType',
        circleBoothApplyDetail.data.userBoothApplication.booth_type,
      )
      setValue(
        'zoneType',
        circleBoothApplyDetail.data.userBoothApplication.zone_type,
      )
    }
  }, [circleBoothApplyDetail])

  return (
    <Wrapper>
      <BackBox onClick={() => navigate('/circle-participation/info')}>
        <Back />
        <span className="text">참가안내로 돌아가기</span>
      </BackBox>
      {circleBoothApplyDetail && (
        <Container>
          <Title>서클 참가 수정</Title>
          <FestivalInfoBox>
            <SubTitle>{ongoingEvent?.data.eventInfo.name}</SubTitle>
            <TagBox>
              <Tag $size="sm" $variant="gray">
                개최 장소
              </Tag>
              <div>{ongoingEvent?.data.eventInfo.place}</div>
            </TagBox>
            <TagBox>
              <Tag $size="sm" $variant="gray">
                개최일
              </Tag>
              <div>
                {ongoingEvent?.data.eventInfo.start_date.slice(0, 11)} ~{' '}
                {ongoingEvent?.data.eventInfo.end_date.slice(0, 11)}
              </div>
            </TagBox>
          </FestivalInfoBox>
          <CircleInfoBox
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => checkKeyDown(e)}
          >
            <SubTitle>서클 정보</SubTitle>
            <div className="warning">
              *결제 요청을 받으시면 그 이후부터는 정보 수정이 불가합니다.
            </div>
            <InputBox className="center">
              <div className="title">서클 이름</div>
              <input
                type="text"
                {...register('boothName')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.booth_name
                }
              />
            </InputBox>

            <div className="horizontal" />
            <InputBox>
              <div className="title">참가 희망일</div>
              <SelectButtonBox>
                {circleBoothform?.data.boothApplication.date_type_list.map(
                  (date: any) => (
                    <SelectButton
                      key={date.code}
                      className={dateType === date.code ? 'active' : ''}
                      onClick={() => {
                        if (
                          boothType === 'B0200004' &&
                          date.code !== 'E0300003'
                        ) {
                          setIsSuperBooth(false)
                          enqueueSnackbar(
                            '대형 부스 이상은 양일 신청 시에만 가능합니다.',
                            { variant: 'error' },
                          )
                        } else {
                          setValue('dateType', date.code)
                        }
                      }}
                    >
                      {dateType === date.code ? (
                        <Check
                          color={theme.colors.white}
                          width={21}
                          height={21}
                        />
                      ) : null}
                      {date.description}
                    </SelectButton>
                  ),
                )}
              </SelectButtonBox>
            </InputBox>
            <div className="warning">
              *대형 부스 이상은 양일 신청 시에만 진행 가능합니다.
            </div>

            <div className="horizontal" />
            <InputBox>
              <div className="title">부스 신청 사양</div>
              <SelectButtonBox className="column">
                {booth_type_list.map((booth: any) => {
                  if (booth.is_option === 'N') {
                    return (
                      <SelectButton
                        key={booth.code}
                        className={boothType === booth.code ? 'active' : ''}
                        onClick={() => {
                          if (
                            booth.code === 'B0200004' &&
                            dateType !== 'E0300003'
                          ) {
                            enqueueSnackbar(
                              '대형 부스 이상은 양일 신청 시에만 가능합니다.',
                              { variant: 'error' },
                            )
                          } else {
                            setIsSuperBooth(false)
                            setValue('boothOptionType', '')
                            setValue('boothType', booth.code)
                          }
                        }}
                      >
                        {boothType === booth.code ? (
                          <Check
                            color={theme.colors.white}
                            width={21}
                            height={21}
                          />
                        ) : null}
                        {booth.description} : {booth.booth_description}
                      </SelectButton>
                    )
                  }
                })}
                {isSuperBooth &&
                  booth_type_list.map((booth: any) => {
                    if (booth.is_option === 'Y') {
                      return (
                        <SelectButton
                          key={booth.code}
                          className={
                            boothOptionType === booth.code ? 'active' : ''
                          }
                          onClick={() => {
                            setValue('boothOptionType', booth.code)
                            setValue('boothType', 'B0200004')
                          }}
                        >
                          {boothOptionType === booth.code ? (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          ) : null}
                          {booth.description} : {booth.booth_description}
                        </SelectButton>
                      )
                    }
                  })}
              </SelectButtonBox>

              {checkOverLargeBoothType(boothType) ? (
                <BubbleBox>
                  <div>초대형부스 업그레이드 희망</div>
                  <div className="button-box">
                    <div
                      className={`button ${isSuperBooth ? 'active' : ''}`}
                      onClick={() => setIsSuperBooth(true)}
                    >
                      {isSuperBooth ? (
                        <Check
                          color={theme.colors.white}
                          width={21}
                          height={21}
                        />
                      ) : null}
                      희망
                    </div>
                    <div
                      className={`button ${!isSuperBooth ? 'active' : ''}`}
                      onClick={() => {
                        setValue('boothType', 'B0200004')
                        setIsSuperBooth(false)
                      }}
                    >
                      {!isSuperBooth ? (
                        <Check
                          color={theme.colors.white}
                          width={21}
                          height={21}
                        />
                      ) : null}
                      비희망
                    </div>
                  </div>
                  <Poligon />
                </BubbleBox>
              ) : null}
            </InputBox>

            <div className="warning">
              *초대형 부스 신청을 요청 하더라도 행사장 사정에따라 신청이 거절될
              수 있으며, 거절된 경우 일반 대형 부스로 신청이 진행됩니다.
            </div>

            <div className="horizontal" />
            <InputBox>
              <div className="title">특별존 참가</div>
              <SelectButtonBox>
                {zone_type_list.map((zone: any) => (
                  <SelectButton
                    key={zone.code}
                    className={zoneType === zone.code ? 'active' : ''}
                    onClick={() => setValue('zoneType', zone.code)}
                  >
                    {zoneType === zone.code ? (
                      <Check
                        color={theme.colors.white}
                        width={21}
                        height={21}
                      />
                    ) : null}
                    {zone.description}
                  </SelectButton>
                ))}
              </SelectButtonBox>
            </InputBox>

            <div className="horizontal" />
            <InputBox className="center">
              <div className="title">대표자명</div>
              <input
                {...register('userName')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.user_name
                }
              />
            </InputBox>

            <div className="horizontal" />
            <InputBox className="center">
              <div className="title">이메일</div>
              <input
                {...register('userEmail')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.user_email
                }
              />
            </InputBox>

            <div className="horizontal" />
            <InputBox className="center">
              <div className="title">대표자 연락처</div>
              <input
                {...register('userMobile')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.user_mobile
                }
              />
              {!checkPhoneNumberValidation(userMobile) && (
                <WarningText>
                  *휴대폰 번호 형식이 유효하지 않습니다.
                </WarningText>
              )}
            </InputBox>

            <div className="horizontal" />
            {/* <InputBox className="center">
              <div className="title">인접 희망 서클 이름</div>
              <input
                {...register('nearBooth')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.near_booth
                }
              />
            </InputBox> */}

            <ButtonBox>
              <SubmitButton type="submit" value="수정 완료" />

              <StyledButton
                $variant="transparent"
                $width={253}
                $height={58}
                $fontSize={24}
                onClick={() => navigate(-1)}
              >
                수정 취소
              </StyledButton>
            </ButtonBox>
          </CircleInfoBox>
        </Container>
      )}
    </Wrapper>
  )
}
export default CircleApplyModifyPage
const Wrapper = styled.section<any>`
  display: flex;
  flex-direction: column;
  gap: 60px;

  width: 100vw;
  max-width: 1080px;
  min-height: calc(100vh - ${theme.size.headerHeight}px);
  margin: 50px auto;
  padding-top: ${theme.size.headerHeight}px;

  @media ${theme.device.medium} {
    gap: 40px;
    width: 90vw;
  }
`
const BackBox = styled.div`
  cursor: pointer;

  display: flex;
  gap: 12px;
  align-items: center;

  margin-top: 20px;

  .text {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
  }
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin: 50px 0;

  @media ${theme.device.medium} {
    gap: 24px;
    margin: 0;
  }
`
const Title = styled.h2`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;

  @media ${theme.device.medium} {
    font-size: 28px;
  }
`
const FestivalInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;
`
const SubTitle = styled.h3`
  margin-bottom: 16px;
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 32px;
  line-height: 44.8px;

  @media ${theme.device.medium} {
    font-size: 21px;
    line-height: 29px;
  }
`
const TagBox = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;
  font-family: NanumSquareNeo-Regular, sans-serif;

  .admin-memo {
    color: ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: start;
  }
`

const CircleInfoBox = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;

  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  .warning {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    line-height: 21px;
    color: ${theme.colors.warning};
  }

  .horizontal {
    width: 100%;
    border-bottom: 1px solid ${theme.colors.grey_2};
  }
`

const InputBox = styled.div`
  position: relative;

  display: flex;
  gap: 20px;

  box-sizing: border-box;
  width: 100%;
  padding: 20px 0;

  &.center {
    align-items: center;

    @media ${theme.device.medium} {
      align-items: start;
    }
  }

  .title {
    min-width: 130px;

    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
    text-align: left;

    @media ${theme.device.medium} {
      width: 100%;
    }
  }

  .desc {
    font-family: NanumSquareNeo-Bold, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
  }

  input {
    box-sizing: border-box;
    min-width: 360px;
    height: 49px;
    padding: 20px;

    font-family: NanumSquareNeo-Regular, sans-serif;

    background-color: ${theme.colors.grey_1};
    border: none;
    border-radius: 12px;

    @media ${theme.device.medium} {
      width: 100%;
      min-width: 100%;
    }
  }

  textarea {
    box-sizing: border-box;
    width: 100%;
    height: auto;
    min-height: 100px;
    padding: 20px;

    font-family: NanumSquareNeo-Regular, sans-serif;

    background-color: ${theme.colors.grey_1};
    border: none;
    border-radius: 12px;

    @media ${theme.device.medium} {
      width: 100%;
    }
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: start;
  }
`
const BubbleBox = styled.div`
  position: relative;
  position: absolute;
  z-index: ${theme.zIndex.aboveDefault};
  top: 200px;
  left: 800px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  box-sizing: border-box;
  width: 270px;
  height: 134px;
  padding: 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border-radius: 12px;

  .button-box {
    display: flex;
    gap: 4px;

    .button {
      cursor: pointer;

      display: flex;
      align-items: center;
      justify-content: center;

      width: 200px;
      height: 49px;

      border: 1px solid ${theme.colors.grey_3};
      border-radius: 12px;

      &.active {
        color: ${theme.colors.white};
        background-color: ${theme.colors.mint_3};
        border: none;
      }
    }
  }
`
const Poligon = styled.div`
  position: absolute;
  z-index: 999;
  top: 20px;
  left: -25px;

  width: 26px;
  height: 26px;

  background-color: ${theme.colors.grey_1};
  clip-path: polygon(50% 50%, 100% 100%, 100% 0);

  @media ${theme.device.medium} {
    top: 80px;
  }
`
const SelectButtonBox = styled.div`
  display: flex;
  flex-flow: column wrap;
  gap: 20px;

  &.column {
    flex-direction: column;
  }

  .divider {
    width: 100%;
    min-width: 700px;
    margin: 20px 0;
    border-bottom: 1px solid ${theme.colors.grey_3};

    @media ${theme.device.large} {
      min-width: 0;
    }
  }
`
const SelectButton = styled.span`
  cursor: pointer;

  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  &.active {
    color: ${theme.colors.white};
    background-color: ${theme.colors.mint_3};
    border: none;
  }

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 15px;
  }
`
const ButtonBox = styled.div`
  display: flex;
  gap: 20px;
`

const SubmitButton = styled.input`
  cursor: pointer;

  gap: 4px;

  width: 148px;
  height: 58px;
  padding: 8px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 24px;
  font-weight: 400;
  color: ${theme.colors.white};

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 50px;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 15px;
  }
`
const StyledButton = styled(Button)`
  width: fit-content;
  font-size: 24px;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 18px;
  }
`

const WarningText = styled.div`
  margin: 10px 5px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.warning};
`
